import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { colors as themeColors, type ColorTokens } from '@/theme/colors';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: 'light' | 'dark';
  colors: ColorTokens;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  forceTheme?: 'light' | 'dark'; // Forzar un tema específico, ignorando localStorage y sistema
}

export function ThemeProvider({ children, defaultTheme = 'system', forceTheme }: ThemeProviderProps) {
  const [theme, setTheme] = useState<Theme>(() => {
    // Si hay un tema forzado, usarlo directamente
    if (forceTheme) return forceTheme;
    if (typeof window === 'undefined') return defaultTheme;
    return (localStorage.getItem('theme') as Theme) || defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => {
    if (forceTheme) return forceTheme;
    if (typeof window === 'undefined') return 'light';
    if (theme === 'system') {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return theme === 'dark' ? 'dark' : 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    let currentResolved: 'light' | 'dark' = 'light';

    const applyTheme = (isDark: boolean) => {
      if (isDark) {
        root.classList.add('dark');
        currentResolved = 'dark';
      } else {
        root.classList.remove('dark');
        currentResolved = 'light';
      }
      setResolvedTheme(currentResolved);
    };

    // Si hay un tema forzado, aplicarlo y salir
    if (forceTheme) {
      applyTheme(forceTheme === 'dark');
      return;
    }

    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mediaQuery.matches);

      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    } else {
      applyTheme(theme === 'dark');
    }
  }, [theme, forceTheme]);

  useEffect(() => {
    // No guardar en localStorage si hay tema forzado
    if (!forceTheme) {
      localStorage.setItem('theme', theme);
    }
  }, [theme, forceTheme]);

  const colors = resolvedTheme === 'dark' ? themeColors.dark : themeColors.light;

  return (
    <ThemeContext.Provider value={{ theme, resolvedTheme, colors, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

/**
 * Hook para acceder directamente a los colores del tema actual
 */
export function useColors(): ColorTokens {
  const { colors } = useTheme();
  return colors;
}
